import React, { useRef, useEffect, useState } from 'react';
import { Users, SlidersHorizontal, FileText, Columns } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { COLUMN_DEFINITIONS } from '../../utils/employeeConstants';
import { Skeleton } from '../ui/skeleton';

const EmployeeTableCard = ({
  filteredEmployees,
  selectedEmployees,
  setSelectedEmployees,
  setShowBulkActions,
  loading,
  selectedDepartement,
  hasAdvancedFilters,
  handleOpenAdvancedFilters,
  handleOpenDocumentsModal,
  documentTemplatesLoading,
  visibleColumns,
  toggleColumnVisibility,
  getVisibleColumnDefs,
  getColumnLabel,
  renderCellContent,
  t,
}) => {
  const topScrollRef = useRef(null);
  const tableRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const MOBILE_COLUMNS = new Set(['select', 'last_name', 'status', 'actions']);
  const getColumns = () => {
    const cols = getVisibleColumnDefs();
    return isMobile ? cols.filter((c) => MOBILE_COLUMNS.has(c.id)) : cols;
  };

  useEffect(() => {
    const topScroll = topScrollRef.current;
    const tableElement = tableRef.current;
    const tableScroll = tableElement?.closest('[data-table-scroll-container]');
    const topScrollContent = topScroll?.firstElementChild;

    if (!topScroll || !tableScroll || !tableElement || !topScrollContent) return;

    let isSyncing = false;

    const syncScrollbarSize = () => {
      const tableWidth = Math.max(tableElement.scrollWidth, tableScroll.clientWidth);
      topScrollContent.style.width = `${tableWidth}px`;
    };

    const handleTopScroll = () => {
      if (isSyncing) return;
      if (Math.abs(tableScroll.scrollLeft - topScroll.scrollLeft) < 1) return;
      isSyncing = true;
      tableScroll.scrollLeft = topScroll.scrollLeft;
      requestAnimationFrame(() => { isSyncing = false; });
    };

    const handleTableScroll = () => {
      if (isSyncing) return;
      if (Math.abs(topScroll.scrollLeft - tableScroll.scrollLeft) < 1) return;
      isSyncing = true;
      topScroll.scrollLeft = tableScroll.scrollLeft;
      requestAnimationFrame(() => { isSyncing = false; });
    };

    syncScrollbarSize();
    const timeouts = [50, 200, 500, 1000].map((d) => setTimeout(syncScrollbarSize, d));

    topScroll.addEventListener('scroll', handleTopScroll);
    tableScroll.addEventListener('scroll', handleTableScroll);

    const resizeObserver = new ResizeObserver(syncScrollbarSize);
    resizeObserver.observe(tableElement);
    resizeObserver.observe(tableScroll);
    window.addEventListener('resize', syncScrollbarSize);

    return () => {
      topScroll.removeEventListener('scroll', handleTopScroll);
      tableScroll.removeEventListener('scroll', handleTableScroll);
      resizeObserver.disconnect();
      window.removeEventListener('resize', syncScrollbarSize);
      timeouts.forEach(clearTimeout);
    };
  }, [filteredEmployees, visibleColumns]);

  const allSelected = selectedEmployees.length === filteredEmployees.length && filteredEmployees.length > 0;

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedEmployees(filteredEmployees.map((emp) => emp.id));
      setShowBulkActions(true);
    } else {
      setSelectedEmployees([]);
      setShowBulkActions(false);
    }
  };

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex-1">
            <CardTitle>{t('employees.title')}</CardTitle>
            <CardDescription>
              {t('employees.totalCount', { count: filteredEmployees.length })}
              {selectedDepartement && <span className="text-primary"> dans {selectedDepartement.name}</span>}
            </CardDescription>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center space-x-2">
              <Checkbox checked={allSelected} onCheckedChange={handleSelectAll} />
              <Label className="text-sm cursor-pointer">{t('common.selectAll')}</Label>
            </div>

            <Button
              variant={hasAdvancedFilters ? 'default' : 'outline'}
              size="sm"
              className="gap-2 whitespace-nowrap"
              onClick={handleOpenAdvancedFilters}
            >
              <SlidersHorizontal className="h-4 w-4" />
              {t('employeesPage.dialogs.advancedFilters')}
              {hasAdvancedFilters && <span className="ml-1 h-2 w-2 rounded-full bg-emerald-500" />}
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="gap-2 whitespace-nowrap"
              onClick={handleOpenDocumentsModal}
              disabled={filteredEmployees.length === 0 || documentTemplatesLoading}
            >
              <FileText className="h-4 w-4" />
              {t('common.print')}
            </Button>

            {!isMobile && <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 whitespace-nowrap">
                  <Columns className="h-4 w-4" />
                  {t('employeesPage.actions.columns')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 max-h-96 overflow-y-auto" align="end">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-sm mb-3">{t('employeesPage.sections.visibleColumns')}</h4>
                    <p className="text-xs text-muted-foreground mb-3">{t('employeesPage.descriptions.selectColumns')}</p>
                  </div>
                  <div className="space-y-2">
                    {COLUMN_DEFINITIONS.filter((col) => !col.fixed).map((column) => (
                      <div key={column.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`col-${column.id}`}
                          checked={visibleColumns.includes(column.id)}
                          onCheckedChange={() => toggleColumnVisibility(column.id)}
                        />
                        <Label htmlFor={`col-${column.id}`} className="text-sm font-normal cursor-pointer">
                          {getColumnLabel(column.id)}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </PopoverContent>
            </Popover>}
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="w-full border rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  {[...Array(6)].map((_, i) => (
                    <TableHead key={i}><Skeleton className="h-4 w-24" /></TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...Array(8)].map((_, row) => (
                  <TableRow key={row}>
                    <TableCell><Skeleton className="h-4 w-4 rounded" /></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
                        <div className="space-y-1">
                          <Skeleton className="h-3.5 w-32" />
                          <Skeleton className="h-3 w-20" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell><Skeleton className="h-3.5 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-3.5 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-16 rounded-full" /></TableCell>
                    <TableCell><Skeleton className="h-7 w-7 ml-auto rounded" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <>
            {!isMobile && (
              <div
                ref={topScrollRef}
                className="w-full overflow-x-scroll mb-3 border rounded-md"
                style={{ overflowY: 'hidden', height: '17px' }}
              >
                <div className="h-4 w-full" />
              </div>
            )}

            <div className="w-full overflow-x-scroll border rounded-md" data-table-scroll-container>
              <Table ref={tableRef} style={{ minWidth: isMobile ? '100%' : `${getVisibleColumnDefs().length * 150}px` }}>
                <TableHeader>
                  <TableRow>
                    {getColumns().map((column) => (
                      <TableHead
                        key={column.id}
                        style={{
                          minWidth: column.id === 'select' ? '40px' : column.id === 'actions' ? (isMobile ? '70px' : '120px') : (isMobile ? '110px' : '180px'),
                          width:    column.id === 'select' ? '40px' : column.id === 'actions' ? (isMobile ? '70px' : '120px') : 'auto',
                        }}
                        className={column.id === 'actions' ? 'text-right' : ''}
                      >
                        {column.id === 'select' ? (
                          <Checkbox checked={allSelected} onCheckedChange={handleSelectAll} />
                        ) : (
                          <div className="whitespace-nowrap">{getColumnLabel(column.id)}</div>
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.map((employee) => (
                    <TableRow key={employee.id}>
                      {getColumns().map((column) => (
                        <TableCell
                          key={column.id}
                          style={{
                            minWidth: column.id === 'select' ? '40px' : column.id === 'actions' ? (isMobile ? '70px' : '120px') : (isMobile ? '110px' : '180px'),
                            width:    column.id === 'select' ? '40px' : column.id === 'actions' ? (isMobile ? '70px' : '120px') : 'auto',
                          }}
                          className={column.id === 'actions' ? 'text-right' : ''}
                        >
                          <div className={column.id === 'actions' ? '' : 'whitespace-nowrap truncate max-w-[110px] sm:max-w-none'}>{renderCellContent(employee, column.id)}</div>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {isMobile && filteredEmployees.length > 0 && !loading && (
          <p className="text-xs text-muted-foreground text-center mt-2 sm:hidden">
            {t('employees.mobileTableHint')}
          </p>
        )}

        {filteredEmployees.length === 0 && !loading && (
          <div className="text-center py-8">
            <Users className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <h3 className="mt-4 text-lg font-semibold">{t('employees.noEmployees')}</h3>
            <p className="text-muted-foreground">
              {t('employees.addEmployee')}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EmployeeTableCard;
